import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Coins, Sparkles, Loader2, Check, FileDown, ExternalLink, X, PartyPopper, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

/** Credit pack purchase widget — listed under the Tools landing page for
 * non-admin users. Uses Stripe Hosted Checkout: backend creates session,
 * we redirect to Stripe, user pays, redirects back with session_id and we
 * poll until paid → credits appear on the balance. */
export default function CreditsShop({ onPurchased }) {
    const { t } = useTranslation();
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [buying, setBuying] = useState(null);
    // Success state after Stripe redirect — drives the download-invoice card
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        api.get("/credits/packs").then((r) => {
            setPacks(r.data.packs || []);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    // Handle return from Stripe — poll until paid
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get("session_id");
        const purchase = params.get("purchase");
        if (purchase === "cancelled") {
            toast.info("Aankoop geannuleerd");
            window.history.replaceState({}, "", window.location.pathname);
            return;
        }
        if (purchase !== "success" || !sessionId) return;

        let attempts = 0;
        const max = 12;
        // After payment is confirmed we keep polling a couple of extra
        // times solely to wait for Stripe to finalize the invoice PDF
        // (usually 1-3 seconds lag).
        let invoiceAttempts = 0;
        const maxInvoice = 6;
        const verifyToastId = toast.loading("Betaling verifiëren…");
        let paidShown = false;

        const poll = async () => {
            attempts += 1;
            try {
                const { data } = await api.get(`/credits/checkout/status/${sessionId}`);
                const isPaid = data.payment_status === "paid" || data.credits_added;
                if (isPaid && !paidShown) {
                    paidShown = true;
                    toast.dismiss(verifyToastId);
                    toast.success(`Betaling gelukt! ${data.credits} credits zijn toegevoegd.`);
                    onPurchased?.();
                    setSuccess({
                        credits: data.credits,
                        invoice_pdf: data.invoice_pdf || null,
                        hosted_invoice_url: data.hosted_invoice_url || null,
                    });
                }
                if (isPaid) {
                    // Already paid → if PDF link is missing keep polling
                    if (!data.invoice_pdf && invoiceAttempts < maxInvoice) {
                        invoiceAttempts += 1;
                        setTimeout(poll, 2000);
                        return;
                    }
                    // PDF available or we gave up → finalize state, drop session_id
                    setSuccess({
                        credits: data.credits,
                        invoice_pdf: data.invoice_pdf || null,
                        hosted_invoice_url: data.hosted_invoice_url || null,
                    });
                    window.history.replaceState({}, "", window.location.pathname);
                    return;
                }
                if (data.status === "expired") {
                    toast.dismiss(verifyToastId);
                    toast.error("Betaalsessie verlopen");
                    window.history.replaceState({}, "", window.location.pathname);
                    return;
                }
                if (attempts < max) {
                    setTimeout(poll, 2500);
                } else {
                    toast.dismiss(verifyToastId);
                    toast.info("Betaling wordt verwerkt — check zo dadelijk je balans");
                    window.history.replaceState({}, "", window.location.pathname);
                }
            } catch {
                if (attempts < max) {
                    setTimeout(poll, 3000);
                } else {
                    toast.dismiss(verifyToastId);
                }
            }
        };
        poll();
    }, [onPurchased]);

    const buy = async (packKey) => {
        setBuying(packKey);
        try {
            const { data } = await api.post("/credits/checkout", {
                package_key: packKey,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aankoop kon niet starten");
            setBuying(null);
        }
    };

    if (loading || packs.length === 0) return null;

    return (
        <div className="mt-12" data-testid="credits-shop">
            {success && (
                <div
                    data-testid="credits-purchase-success"
                    className="mb-8 relative overflow-hidden border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.08] to-emerald-500/[0.02] rounded-sm p-6"
                >
                    <button
                        type="button"
                        onClick={() => setSuccess(null)}
                        data-testid="credits-purchase-success-close"
                        className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-950 transition"
                        aria-label="Sluiten"
                    >
                        <X className="h-4 w-4" />
                    </button>
                    <div className="flex items-start gap-4">
                        <div className="shrink-0 h-10 w-10 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                            <PartyPopper className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-600 mb-1">
                                {t("tools.purchase_success_eyebrow")}
                            </p>
                            <h3 className="font-display text-2xl font-bold tracking-tight mb-1">
                                {t("tools.purchase_success_title", { credits: success.credits })}
                            </h3>
                            <p className="text-sm text-zinc-500 mb-4">
                                {t("tools.purchase_success_desc")}
                            </p>
                            <div className="flex flex-wrap gap-2.5">
                                {success.invoice_pdf ? (
                                    <a
                                        href={success.invoice_pdf}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        download
                                        data-testid="credits-invoice-download-pdf"
                                        className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-white text-zinc-950 font-bold text-sm hover:bg-zinc-100 transition"
                                    >
                                        <FileDown className="h-4 w-4" />
                                        {t("tools.download_invoice")}
                                    </a>
                                ) : (
                                    <span className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-zinc-100 border border-zinc-200 text-zinc-500 text-sm">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        {t("tools.invoice_preparing")}
                                    </span>
                                )}
                                {success.hosted_invoice_url && (
                                    <a
                                        href={success.hosted_invoice_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="credits-invoice-view-online"
                                        className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-zinc-300 text-zinc-950 font-medium text-sm hover:bg-zinc-100 transition"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        {t("tools.view_invoice_online")}
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="flex items-baseline justify-between mb-5">
                <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#22c55e] mb-2 inline-flex items-center gap-2">
                        <Coins className="h-3.5 w-3.5" /> {t("tools.credits_packs_eyebrow")}
                    </p>
                    <h2 className="font-display text-3xl font-bold tracking-tight">{t("tools.credits_packs_title")}</h2>
                </div>
                <div className="hidden md:flex items-center gap-4">
                    <p className="text-xs text-zinc-500">
                        {t("tools.credits_packs_subtitle")}
                    </p>
                    <Link
                        to="/tools/invoices"
                        data-testid="credits-shop-invoices-link"
                        className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-950 transition border-l border-zinc-200 pl-4"
                    >
                        <Receipt className="h-3.5 w-3.5" /> {t("navbar.my_invoices")}
                    </Link>
                </div>
            </div>
            {/* Wat kan ik met credits? — maakt de pakketprijzen tastbaar */}
            <div className="mb-6 bg-white border border-zinc-200 rounded-sm px-5 py-4" data-testid="credits-usage-table">
                <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-3">{t("tools.usage_title")}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                    {[
                        [t("tools.usage_retouch"), t("tools.usage_credit_one")],
                        [t("tools.usage_upscale"), t("tools.usage_credit_one")],
                        [t("tools.usage_blur"), t("tools.usage_credit_one")],
                        [t("tools.usage_video"), t("tools.usage_credit_video"), true],
                    ].map(([label, cost, hl]) => (
                        <div key={label} className="border-b border-dashed border-zinc-200 pb-2">
                            <div className="text-zinc-600">{label}</div>
                            <div className={`mt-0.5 font-bold tabular-nums ${hl ? "text-[#E5A93D]" : "text-zinc-950"}`}>{cost}</div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {packs.map((p) => {
                    const isMost = p.key === "pro";
                    return (
                        <div
                            key={p.key}
                            data-testid={`credit-pack-${p.key}`}
                            className={`relative bg-white border rounded-sm p-6 transition-all ${
                                isMost
                                    ? "border-[#E5A93D]/40 ring-1 ring-[#E5A93D]/20"
                                    : "border-zinc-200 hover:border-zinc-400"
                            }`}
                        >
                            {isMost && (
                                <span className="absolute -top-2.5 left-5 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#E5A93D] text-black font-bold rounded-sm">
                                    ⭐ {p.tagline || "Meest gekozen"}
                                </span>
                            )}
                            <div className="flex items-baseline justify-between mb-2">
                                <h3 className="font-display text-2xl font-bold">{p.label}</h3>
                                <div className="text-right">
                                    <p className="text-3xl font-bold tabular-nums leading-none" style={{ color: isMost ? "#E5A93D" : "#09090B" }}>
                                        €{p.price_eur.toFixed(0)}
                                    </p>
                                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{t("common.excl_vat")}</p>
                                </div>
                            </div>
                            <p className="text-sm text-zinc-500 mb-1">
                                <span className="text-zinc-950 font-semibold tabular-nums">{p.credits}</span> {t("common.credits")}
                            </p>
                            <p className="text-xs text-zinc-500 mb-2">
                                {t("tools.per_credit", { amount: p.per_credit.toFixed(2) })}
                            </p>
                            <p className={`text-xs font-medium mb-5 ${isMost ? "text-[#E5A93D]" : "text-zinc-600"}`} data-testid={`credit-pack-equiv-${p.key}`}>
                                {p.credits >= 15
                                    ? t("tools.pack_equiv_video", { credits: p.credits, videos: Math.floor(p.credits / 15) })
                                    : t("tools.pack_equiv_photo", { credits: p.credits })}
                            </p>
                            <ul className="text-xs text-zinc-500 space-y-1.5 mb-6">
                                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-600 shrink-0" /> {t("tools.feature_instant")}</li>
                                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-600 shrink-0" /> {t("tools.feature_no_expiry")}</li>
                            </ul>
                            <Button
                                onClick={() => buy(p.key)}
                                disabled={buying === p.key}
                                data-testid={`credit-pack-buy-${p.key}`}
                                className={`w-full h-11 rounded-sm font-bold ${
                                    isMost
                                        ? "bg-[#E5A93D] hover:bg-[#C8922C] text-black"
                                        : "bg-white text-zinc-950 hover:bg-zinc-100"
                                }`}
                            >
                                {buying === p.key ? (
                                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("tools.buying")}</>
                                ) : (
                                    <><Sparkles className="h-4 w-4 mr-2" /> {t("tools.purchase", { label: p.label })}</>
                                )}
                            </Button>
                        </div>
                    );
                })}
            </div>
            <p className="mt-4 text-[11px] text-zinc-400 text-center" data-testid="credits-invoice-note">{t("tools.invoice_note")}</p>
            <p className="mt-1 text-[11px] text-zinc-400 text-center" data-testid="credits-terms-note">
                <Link to="/voorwaarden" target="_blank" className="hover:text-zinc-600 underline underline-offset-2">Algemene voorwaarden · Conditions générales · Terms</Link>
            </p>
            {/* Mobile-only link to invoice history (desktop link sits in header) */}
            <div className="md:hidden mt-6 text-center">
                <Link
                    to="/tools/invoices"
                    data-testid="credits-shop-invoices-link-mobile"
                    className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-950 transition"
                >
                    <Receipt className="h-3.5 w-3.5" /> {t("navbar.my_invoices")}
                </Link>
            </div>
        </div>
    );
}
